import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { describeRow, groupPending, fmtDay } from '../lib/weekly-grid.js';

// Manager-only inbox for pending self-submitted availability changes.
// Lives at the top of the Schedule section so managers see them before
// they reach the schedule builder. Renders nothing when there's no
// pending queue. CJ feedback 2026-06-15:
//   "Availability changes do need manager acknowledgement. Approval
//    isn't necessary if it's at least 2 weeks prior to the schedule it
//    affects. Managers just need to be aware that it is changing."
//
// A weekly grid save (docs/AVAILABILITY-WEEKLY-GRID-SPEC.md) arrives as several
// rows sharing a submission_id. It is shown and decided as one change — the
// server acknowledges or rejects the whole save from any one of its rows.
export function AvailabilityChangesInbox({ currentUser }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [pending, setPending] = useState(null); // null = loading, [] = empty
  const [busyKey, setBusyKey] = useState(null);

  async function load() {
    try {
      const r = await api('GET', '/api/availability/pending', null, headers);
      setPending(Array.isArray(r?.pending) ? r.pending : []);
    } catch (_) { setPending([]); }
  }
  useEffect(() => { load(); }, []);

  async function acknowledge(item) {
    setBusyKey(item.key);
    try {
      await api('POST', `/api/availability/reductions/${item.rows[0].id}/acknowledge`, {}, headers);
      load();
    } catch (e) { alert(e.message || 'Acknowledge failed'); }
    finally { setBusyKey(null); }
  }
  async function reject(item) {
    const note = window.prompt(`Reject this availability change?\n\nOptional note for ${item.rows[0].employee_name}:`, '');
    if (note === null) return;
    setBusyKey(item.key);
    try {
      await api('POST', `/api/availability/reductions/${item.rows[0].id}/reject`, { note: note || null }, headers);
      load();
    } catch (e) { alert(e.message || 'Reject failed'); }
    finally { setBusyKey(null); }
  }

  if (!pending || pending.length === 0) return null;
  const items = groupPending(pending);

  return (
    <div className="card" style={{ borderLeft: '4px solid #fb923c', padding: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#9a3412', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
        {items.length} availability change{items.length === 1 ? '' : 's'} need{items.length === 1 ? 's' : ''} your review
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((item) => {
          const first = item.rows[0];
          const needsApproval = !!first.approval_required;
          const busy = busyKey === item.key;
          return (
            <div key={item.key} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
                                           padding: '8px 10px', background: '#fafafa', borderRadius: 6 }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#212121' }}>
                  {first.employee_name}
                  <span style={{ fontSize: 10, color: '#9e9e9e', fontWeight: 500, marginLeft: 6 }}>· {first.employee_role || 'Staff'}</span>
                </div>
                {first.submission_id && (
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#37474f' }}>
                    New weekly availability from {fmtDay(first.effective_start_date)}
                  </div>
                )}
                {item.rows.map((r) => (
                  <div key={r.id} style={{ fontSize: 12, color: '#37474f' }}>{describeRow(r)}</div>
                ))}
                {first.note && <div style={{ fontSize: 11, color: '#666', fontStyle: 'italic' }}>"{first.note}"</div>}
                <div style={{ fontSize: 10, color: needsApproval ? '#9a3412' : '#9e9e9e', marginTop: 2 }}>
                  {needsApproval ? '< 2 weeks lead time — approval needed' : '≥ 2 weeks lead time — just acknowledge'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => acknowledge(item)} disabled={busy}
                  style={{ background: needsApproval ? '#16a34a' : 'var(--forest)', color: '#fff',
                            border: 'none', borderRadius: 6, padding: '6px 12px',
                            fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                  {busy ? '…' : (needsApproval ? 'Approve' : 'Acknowledge')}
                </button>
                <button onClick={() => reject(item)} disabled={busy}
                  style={{ background: '#fff', color: '#991b1b',
                            border: '1px solid #fecaca', borderRadius: 6, padding: '6px 10px',
                            fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                  Reject
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
